import React, { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";
import { auth } from "../auth/firebase"; // Set up Firebase in a separate file
import { PublicClientApplication } from "@azure/msal-browser";
import { msalConfig } from "../auth/authConfig";

// Replace with your actual Stripe public key
const stripePromise = loadStripe("pk_live_51QpeeLBn0wB7oKhyBnTQOZS9xNCah6xmfCSDwd4fA24p3RkOTiZZiaZ3zlIaUpxMJbeAxNrABOBMONCR8zr1thbd00c6jwO0P2");
const msalInstance = new PublicClientApplication(msalConfig);
const API_URL = "https://server-delta-vert.vercel.app/api";
const VERIFIED_CHECKOUT_STORAGE_KEY = "softestimateVerifiedCheckout";
const PENDING_CHECKOUT_EMAIL_STORAGE_KEY = "softestimatePendingCheckoutEmail";
const REDITUS_CONVERSION_STORAGE_KEY = "softestimateReditusConversion";

const normalizeEmail = (value = "") => String(value).trim().toLowerCase();

const isValidEmail = (value = "") => /[^\s@]+@[^\s@]+\.[^\s@]+/.test(normalizeEmail(value));

const getPlanLabel = (plan) => {
    switch (plan) {
        case "individual":
            return "Individual";
        case "business":
            return "Business";
        default:
            return "Subscription";
    }
};

const Home = ({ onSubscriptionSuccess }) => {
    const [promoCode, setPromoCode] = useState("");
    const [promoApplied, setPromoApplied] = useState(false);
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [isMsalReady, setIsMsalReady] = useState(false);
    const [checkoutSessionId, setCheckoutSessionId] = useState("");
    const [paymentVerificationStatus, setPaymentVerificationStatus] = useState("idle");
    const [paymentVerificationError, setPaymentVerificationError] = useState("");
    const [paymentSuccessData, setPaymentSuccessData] = useState(null);

    const clearCheckoutQueryParams = () => {
        if (window.location.search) {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    };

    const clearVerifiedCheckoutSession = () => {
        sessionStorage.removeItem(VERIFIED_CHECKOUT_STORAGE_KEY);
        setCheckoutSessionId("");
        setPaymentSuccessData(null);
        setPaymentVerificationStatus("idle");
        setPaymentVerificationError("");
        clearCheckoutQueryParams();
    };

    const persistVerifiedCheckoutSession = (sessionData) => {
        sessionStorage.setItem(VERIFIED_CHECKOUT_STORAGE_KEY, JSON.stringify(sessionData));
        setCheckoutSessionId(sessionData.sessionId);
        setPaymentSuccessData(sessionData);
        setPaymentVerificationStatus("verified");
        setPaymentVerificationError("");
        clearCheckoutQueryParams();
    };

    const trackReditusConversionOnce = (conversionEmail, sessionId = "", attempt = 0) => {
        const normalizedEmail = normalizeEmail(conversionEmail);

        if (!normalizedEmail) {
            console.warn("Reditus conversion skipped: missing email.");
            return;
        }

        const dedupeKey = sessionId || normalizedEmail;
        const existingKey = sessionStorage.getItem(REDITUS_CONVERSION_STORAGE_KEY);

        if (existingKey === dedupeKey) {
            return;
        }

        if (typeof window === "undefined" || typeof window.gr !== "function") {
            if (attempt < 5) {
                window.setTimeout(() => {
                    trackReditusConversionOnce(normalizedEmail, sessionId, attempt + 1);
                }, 1000);
            } else {
                console.warn("Reditus conversion skipped: tracking script not available.");
            }
            return;
        }

        try {
            window.gr("track", "conversion", { email: normalizedEmail });
            sessionStorage.setItem(REDITUS_CONVERSION_STORAGE_KEY, dedupeKey);
            console.log("✅ Reditus conversion tracked for:", normalizedEmail);
        } catch (error) {
            console.error("❌ Reditus conversion tracking failed:", error);
        }
    };

    const completeSubscriptionAccess = () => {
        localStorage.setItem("isSubscribed", "true");
        clearVerifiedCheckoutSession();
        onSubscriptionSuccess();
    };

    const verifyCheckoutSession = async (sessionId) => {
        if (!sessionId) {
            setPaymentVerificationStatus("error");
            setPaymentVerificationError("Missing checkout session information. Please try again.");
            return;
        }

        setCheckoutSessionId(sessionId);
        setPaymentVerificationStatus("verifying");
        setPaymentVerificationError("");

        try {
            const response = await fetch(`${API_URL}/checkout-session-status?session_id=${encodeURIComponent(sessionId)}`);
            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || "Unable to verify your payment right now.");
            }

            if (!data.verified) {
                throw new Error(data.error || "Your payment has not been confirmed yet. If you were charged, please wait a moment and refresh.");
            }

            persistVerifiedCheckoutSession({
                sessionId,
                emailHint: data.emailHint || "",
                plan: data.plan || "",
                subscriptionStatus: data.subscriptionStatus || "",
            });

            const checkoutEmail = normalizeEmail(
                sessionStorage.getItem(PENDING_CHECKOUT_EMAIL_STORAGE_KEY) || email
            );

            if (checkoutEmail) {
                trackReditusConversionOnce(checkoutEmail, sessionId);
            }
        } catch (error) {
            console.error("Error verifying checkout session:", error);
            setPaymentVerificationStatus("error");
            setPaymentVerificationError(error.message || "Unable to verify your payment right now.");
        }
    };

    useEffect(() => {
        const queryParams = new URLSearchParams(window.location.search);
        const checkoutStatus = queryParams.get("checkout");
        const sessionId = queryParams.get("session_id");
        const failedStatus = queryParams.get("status");

        const storedCheckoutEmail = normalizeEmail(sessionStorage.getItem(PENDING_CHECKOUT_EMAIL_STORAGE_KEY) || "");
        if (storedCheckoutEmail && !email) {
            setEmail(storedCheckoutEmail);
        }

        if (failedStatus === "failed") {
            alert("Subscription process could not complete. Please try again.");
            clearCheckoutQueryParams();
            return;
        }

        if (checkoutStatus === "success" && sessionId) {
            verifyCheckoutSession(sessionId);
            return;
        }

        const storedCheckout = sessionStorage.getItem(VERIFIED_CHECKOUT_STORAGE_KEY);
        if (!storedCheckout) {
            return;
        }

        try {
            const parsedCheckout = JSON.parse(storedCheckout);
            if (parsedCheckout?.sessionId) {
                setCheckoutSessionId(parsedCheckout.sessionId);
                setPaymentSuccessData(parsedCheckout);
                setPaymentVerificationStatus("verified");
                setPaymentVerificationError("");
            }
        } catch (error) {
            console.error("Error restoring payment success state:", error);
            sessionStorage.removeItem(VERIFIED_CHECKOUT_STORAGE_KEY);
        }
    }, []);

    useEffect(() => {
        if (paymentVerificationStatus !== "verified" || !paymentSuccessData?.sessionId) {
            return;
        }

        const checkoutEmail = normalizeEmail(
            sessionStorage.getItem(PENDING_CHECKOUT_EMAIL_STORAGE_KEY) || email
        );

        if (checkoutEmail) {
            trackReditusConversionOnce(checkoutEmail, paymentSuccessData.sessionId);
        }
    }, [paymentVerificationStatus, paymentSuccessData, email]);

    useEffect(() => {
        const initializeMsal = async () => {
            try {
                await msalInstance.initialize(); // ✅ Ensure MSAL is initialized
                await msalInstance.handleRedirectPromise(); // ✅ Wait for MSAL to resolve redirects
                setIsMsalReady(true);
            } catch (error) {
                console.error("MSAL Initialization Error:", error);
            }
        };

        initializeMsal();
    }, []);

    const checkMfaStatus = async (accessToken, email) => {
        try {
            // Skip MFA check for personal Microsoft accounts (Hotmail, Outlook)
            if (email.includes("@hotmail.com") || email.includes("@outlook.com") || email.includes("@live.com")) {
                console.warn("⚠️ Skipping MFA check for personal Microsoft account:", email);
                return true;  // Assume MFA is enabled
            }

            const response = await fetch("https://graph.microsoft.com/v1.0/me/authentication/methods", {
                method: "GET",
                headers: { Authorization: `Bearer ${accessToken}` },
            });

            if (!response.ok) {
                const errorResponse = await response.text();
                console.error("❌ MFA API Failed:", errorResponse);
                return false;
            }

            const data = await response.json();
            console.log("✅ MFA API Response:", JSON.stringify(data, null, 2));

            return data.value && data.value.length > 0;
        } catch (error) {
            console.error("❌ Error fetching MFA status:", error);
            return false;
        }
    };


    const signInWithMicrosoft = async () => {
        try {
            if (!isMsalReady) {
                alert("MSAL is still initializing. Please try again.");
                return;
            }

            const loginResponse = await msalInstance.loginPopup({
                scopes: ["openid", "email", "profile"],
                prompt: "select_account",
            });

            if (!loginResponse.account) {
                throw new Error("❌ No Microsoft account found after login.");
            }

            const accessToken = loginResponse.idToken;

            if (!accessToken) {
                console.error("❌ Microsoft Sign-In Error: No access token received.");
                alert("Microsoft login failed: No access token received.");
                return;
            }

            sessionStorage.setItem("microsoftAccessToken", accessToken); // Store token
            setEmail(normalizeEmail(loginResponse.account.username || ""));

            // Validate the subscription
            validateMicrosoftSubscription(loginResponse.account.username, accessToken);
        } catch (error) {
            console.error("❌ Microsoft Sign-In Error:", error);
            alert(`Microsoft login failed: ${error.message}`);
        }
    };


    const signInWithGoogle = async () => {
        try {
            const provider = new GoogleAuthProvider();
            // Force account selection every time
            provider.setCustomParameters({
                prompt: "select_account"
            });
            const result = await signInWithPopup(auth, provider);
            const user = result.user;

            await user.getIdToken();

            if (user) {
                setEmail(normalizeEmail(user.email || ""));
                validateSubscription(user.email, "google"); // Check subscription
            }
        } catch (error) {
            console.error("Google Sign-In Error:", error);
            alert("Google login failed. Please try again.");
        }
    };


    const handleSubscribe = async (plan) => {
        if (promoApplied) {
            alert("Promo applied! You can start using the app.");
            completeSubscriptionAccess();
            return;
        }

        const normalizedEmail = normalizeEmail(email);

        if (!normalizedEmail) {
            alert("Please enter the email you will use for checkout and sign-in before subscribing.");
            return;
        }

        if (!isValidEmail(normalizedEmail)) {
            alert("Please enter a valid email address before subscribing.");
            return;
        }

        setEmail(normalizedEmail);
        sessionStorage.setItem(PENDING_CHECKOUT_EMAIL_STORAGE_KEY, normalizedEmail);

        try {
            const response = await fetch(`${API_URL}/create-checkout-session`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ plan, email: normalizedEmail }),
            });

            const session = await response.json();

            if (!response.ok) {
                throw new Error(session.error || "Failed to create Stripe session. Please try again.");
            }

            if (session.id) {
                const stripe = await stripePromise;
                const { error } = await stripe.redirectToCheckout({ sessionId: session.id });

                if (error) {
                    console.error("Stripe Checkout Error:", error);
                    alert(error.message);
                }
            } else {
                alert(session.error || "Failed to create Stripe session. Please try again.");
            }
        } catch (error) {
            console.error("Error in subscription:", error);
            alert(error.message || "Error in processing subscription.");
        }
    };

    const validateSubscription = async (email) => {
        const normalizedEmail = normalizeEmail(email);

        if (!normalizedEmail) {
            alert("Please enter your email.");
            return;
        }

        setLoading(true);
        try {
            const idToken = await auth.currentUser.getIdToken();  // ✅ Only for Google Auth
            const response = await fetch(`${API_URL}/validate-subscription`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${idToken}`,
                },
                body: JSON.stringify({ email: normalizedEmail }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || data.message || "Error validating subscription.");
            }

            if (data.valid) {
                completeSubscriptionAccess();
            } else {
                alert("No active subscription found for this email.");
            }
        } catch (error) {
            console.error("Error validating subscription:", error);
            alert(error.message || "Error validating subscription.");
        } finally {
            setLoading(false);
        }
    };

    const validateMicrosoftSubscription = async (email) => {
        const normalizedEmail = normalizeEmail(email);

        if (!normalizedEmail) {
            alert("Please enter your email.");
            return;
        }

        const token = sessionStorage.getItem("microsoftAccessToken"); // ✅ Retrieve token from sessionStorage

        if (!token) {
            alert("❌ No authorization token found.");
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/ms-validate-subscription`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`,
                },
                body: JSON.stringify({ email: normalizedEmail })
            });

            if (!response.ok) {
                const errorResponse = await response.json();
                console.error("❌ Server Response:", errorResponse);
                alert(`Error: ${errorResponse.error}`);
                return;
            }

            const data = await response.json();
            if (data.valid) {
                completeSubscriptionAccess();
            } else {
                alert("⚠ No active Microsoft subscription found.");
            }
        } catch (error) {
            console.error("❌ Error validating Microsoft subscription:", error);
            alert("Error validating subscription.");
        } finally {
            setLoading(false);
        }
    };



    const validPromoCodes = {
        FAMILY50: { expires: "2026-12-31" }, // Add more promo codes with expiry dates
        BILLAHSHETU50: { expires: "2030-12-31" }, // Add more promo codes with expiry dates
        WELCOME10: { expires: "2026-06-30" }, // Example: Another promo code
        WELCOME2026: { expires: "2026-06-30" },

    };

    const isPromoValid = (code) => {
        if (!validPromoCodes[code]) return false;

        const expiryDate = new Date(validPromoCodes[code].expires);
        const currentDate = new Date();

        return currentDate <= expiryDate;
    };

    const applyPromo = () => {
        const trimmedCode = promoCode.trim().toUpperCase();

        if (isPromoValid(trimmedCode)) {
            setPromoApplied(true);
            completeSubscriptionAccess();
        } else {
            console.warn("Invalid or expired promo code.");
        }
    };


    const resetSubscription = () => {
        localStorage.removeItem("isSubscribed");
        window.location.reload(); // Forces re-authentication
    };

    const renderAuthButtons = (wrapperClassName = "") => (
        <div className={`flex flex-col md:flex-row justify-center items-center gap-3 ${wrapperClassName}`.trim()}>
            <button
                onClick={signInWithGoogle}
                disabled={loading}
                className={`bg-red-500 text-white py-2 px-6 rounded-lg hover:bg-red-600 transition ${loading ? "opacity-70 cursor-not-allowed" : ""}`.trim()}
            >
                {loading ? "Validating..." : "Sign in with Google"}
            </button>

            <button
                onClick={signInWithMicrosoft}
                disabled={loading}
                className={`bg-blue-500 text-white py-2 px-6 rounded-lg hover:bg-blue-600 transition ${loading ? "opacity-70 cursor-not-allowed" : ""}`.trim()}
            >
                {loading ? "Validating..." : "Sign in with Microsoft"}
            </button>
        </div>
    );

    const showPaymentSuccessScreen = paymentVerificationStatus === "verifying" || paymentVerificationStatus === "error" || Boolean(paymentSuccessData);

    return (
        <Elements stripe={stripePromise}>
            <div className="bg-gray-100 text-gray-800 min-h-screen flex flex-col items-center w-full">
                {showPaymentSuccessScreen ? (
                    <section className="w-full min-h-screen bg-gradient-to-b from-blue-500 to-blue-600 text-white flex items-center justify-center px-4 py-16">
                        <div className="max-w-3xl w-full bg-white text-gray-800 rounded-2xl shadow-2xl p-8 md:p-10">
                            {paymentVerificationStatus === "verifying" && (
                                <div className="text-center">
                                    <div className="text-sm font-semibold uppercase tracking-[0.2em] text-blue-500">Verifying Payment</div>
                                    <h1 className="text-3xl md:text-4xl font-bold mt-4">Hang tight — we are confirming your Stripe checkout.</h1>
                                    <p className="mt-4 text-lg text-gray-600">
                                        Once confirmed, you will be able to sign in and start using SoftEstimate immediately.
                                    </p>
                                </div>
                            )}

                            {paymentVerificationStatus === "error" && (
                                <div className="text-center">
                                    <div className="text-sm font-semibold uppercase tracking-[0.2em] text-red-500">Payment Verification Needed</div>
                                    <h1 className="text-3xl md:text-4xl font-bold mt-4">We could not confirm your payment yet.</h1>
                                    <p className="mt-4 text-lg text-gray-600">
                                        {paymentVerificationError || "If you completed checkout, give Stripe a moment and try again."}
                                    </p>

                                    <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
                                        <button
                                            onClick={() => verifyCheckoutSession(checkoutSessionId)}
                                            disabled={!checkoutSessionId}
                                            className={`bg-blue-500 text-white py-2 px-6 rounded-lg hover:bg-blue-600 transition ${!checkoutSessionId ? "opacity-70 cursor-not-allowed" : ""}`.trim()}
                                        >
                                            Retry Verification
                                        </button>
                                        <button
                                            onClick={clearVerifiedCheckoutSession}
                                            className="bg-gray-200 text-gray-800 py-2 px-6 rounded-lg hover:bg-gray-300 transition"
                                        >
                                            Back to Plans
                                        </button>
                                    </div>
                                </div>
                            )}

                            {paymentSuccessData && paymentVerificationStatus === "verified" && (
                                <div className="text-center">
                                    <div className="text-sm font-semibold uppercase tracking-[0.2em] text-green-600">Payment Confirmed</div>
                                    <h1 className="text-3xl md:text-4xl font-bold mt-4">Your subscription is active. Now let’s unlock SoftEstimate.</h1>
                                    <p className="mt-4 text-lg text-gray-600">
                                        Sign in with the same email used during checkout so we can match your subscription cleanly and keep billing and referral attribution accurate.
                                    </p>

                                    <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                                        <div className="bg-green-50 border border-green-100 rounded-xl p-4">
                                            <div className="text-sm font-semibold text-green-700 uppercase tracking-wide">Plan</div>
                                            <div className="text-lg font-bold text-gray-900 mt-1">{getPlanLabel(paymentSuccessData.plan)}</div>
                                        </div>
                                        <div className="bg-blue-50 border border-blue-100 rounded-xl p-4">
                                            <div className="text-sm font-semibold text-blue-700 uppercase tracking-wide">Checkout Email</div>
                                            <div className="text-lg font-bold text-gray-900 mt-1">{paymentSuccessData.emailHint || "Use the same email you used during payment"}</div>
                                        </div>
                                    </div>

                                    <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-xl text-left">
                                        <div className="font-semibold text-yellow-800">Important</div>
                                        <p className="mt-2 text-gray-700">
                                            Do not use a different Google or Microsoft account here. If the login email does not match the paid subscription, access validation will fail. Software is honest. Sometimes brutally so.
                                        </p>
                                    </div>

                                    {renderAuthButtons("mt-8")}

                                    <button
                                        onClick={clearVerifiedCheckoutSession}
                                        className="mt-6 bg-gray-200 text-gray-800 py-2 px-6 rounded-lg hover:bg-gray-300 transition"
                                    >
                                        Back to Plans
                                    </button>
                                </div>
                            )}
                        </div>
                    </section>
                ) : (
                    <>
                        {/* Header */}
                        <header className="bg-blue-500 text-white py-10 w-full text-center">
                            <h1 className="text-4xl font-bold">Welcome to SoftEstimate</h1>
                            <p className="mt-4 text-lg">The fastest, most private way to estimate your project. Get accurate cost, time, and resource estimates in minutes—no data storage, no risk. Perfect for Agile, Waterfall, or Hybrid projects.</p>
                            <a href="#subscription" className="bg-yellow-400 text-black text-xl px-6 py-3 mt-6 inline-block rounded-lg hover:bg-yellow-500 transition">
                                Start Estimating Now
                            </a>
                        </header>

                        {/* Key Features */}
                        <section className="py-16 bg-gray-50 text-center w-full">
                            <h2 className="text-3xl font-bold mb-6">Why SoftEstimate?</h2>
                            <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div className="p-5 bg-white shadow-md rounded-lg">
                                    <h3 className="text-lg font-semibold">🚀 Instant Estimates</h3>
                                    <p className="mt-2 text-gray-700">Quickly estimate time, cost, and resources with ease.</p>
                                </div>

                                <div className="p-5 bg-white shadow-md rounded-lg">
                                    <h3 className="text-lg font-semibold">📊 Smart Resource Planning</h3>
                                    <p className="mt-2 text-gray-700">Optimize workload distribution for efficient execution.</p>
                                </div>

                                <div className="p-5 bg-white shadow-md rounded-lg">
                                    <h3 className="text-lg font-semibold">💰 Budget & Cost Control</h3>
                                    <p className="mt-2 text-gray-700">Track expenses, adjust budgets, and stay within limits.</p>
                                </div>

                                <div className="p-5 bg-white shadow-md rounded-lg">
                                    <h3 className="text-lg font-semibold">🔄 Agile & Flexible Planning</h3>
                                    <p className="mt-2 text-gray-700">Supports Agile, Waterfall, and hybrid project methods.</p>
                                </div>

                                <div className="p-5 bg-white shadow-md rounded-lg">
                                    <h3 className="text-lg font-semibold">🔒 100% Private & Secure</h3>
                                    <p className="mt-2 text-gray-700">No data storage—your estimates stay with you.</p>
                                </div>

                                <div className="p-5 bg-white shadow-md rounded-lg">
                                    <h3 className="text-lg font-semibold">📁 Easy Export & Integration</h3>
                                    <p className="mt-2 text-gray-700">Download estimates in CSV for seamless reporting.</p>
                                </div>
                            </div>

                        </section>

                        {/* Subscription Section */}
                        <section className="py-16 text-center bg-white w-full" id="subscription">
                            <h2 className="text-3xl font-bold mb-6">Choose Your Plan</h2>
                            <div className="max-w-3xl mx-auto mb-8 px-4">
                                <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 text-left shadow-sm">
                                    <label htmlFor="subscription-email" className="block text-sm font-semibold text-blue-800 uppercase tracking-wide">
                                        Subscription Email
                                    </label>
                                    <p className="mt-2 text-gray-700">
                                        Enter the email you will use at checkout and again when you sign in. This keeps Stripe validation clean and blocks accidental duplicate subscriptions.
                                    </p>
                                    <input
                                        id="subscription-email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(normalizeEmail(e.target.value))}
                                        placeholder="name@company.com"
                                        className="mt-4 w-full p-3 border border-blue-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
                                        autoComplete="email"
                                    />
                                </div>
                            </div>
                            <div className="max-w-4xl mx-auto flex flex-col md:flex-row justify-center items-stretch gap-8">

                                <div className="p-6 bg-gray-50 shadow-md rounded-lg w-full md:w-96 flex flex-col items-center text-center">
                                    <h3 className="text-2xl font-semibold text-blue-500">🎉 Limited Time Offer!</h3>
                                    <p className="text-lg text-gray-600 mb-2">Get full access at our lowest price ever.</p>

                                    <p className="text-4xl font-bold text-gray-800">$2.99 <span className="text-lg font-medium text-gray-600">/ month</span></p>
                                    {/* <p className="text-sm text-gray-600 line-through">$12.00/month</p>
                                    <p className="text-sm text-gray-600">Sign up now—prices will increase in June for all subscriptions!</p> */}

                                    <p className="text-xs text-gray-400 mt-1">*Monthly subscription. Limited Estimates. No refunds, but you may cancel anytime.</p>

                                    <button
                                        onClick={() => handleSubscribe("individual")}
                                        className="bg-blue-500 text-white py-2 px-6 rounded-lg mt-4 hover:bg-blue-600 transition"
                                    >
                                        Subscribe Now
                                    </button>
                                </div>

                                <div className="p-6 bg-gray-50 shadow-md rounded-lg w-full md:w-96 flex flex-col items-center text-center">
                                    <h3 className="text-2xl font-semibold text-blue-500">🚀 Exclusive Business Savings!</h3>
                                    <p className="text-lg text-gray-600 mb-2">Effortless estimates for businesses</p>

                                    <p className="text-4xl font-bold text-gray-800">$4.99 <span className="text-lg font-medium text-gray-600">/ month</span></p>
                                    {/* <p className="text-sm text-gray-600 line-through">$24.99/month</p>
                                    <p className="text-sm text-gray-600">Sign up now—prices will increase in June for all subscriptions!</p> */}

                                    <p className="text-xs text-gray-400 mt-1">*Monthly subscription. Unlimited Estimates! No refunds, but you may cancel anytime.</p>

                                    <button
                                        onClick={() => handleSubscribe("business")}
                                        className="bg-blue-500 text-white py-2 px-6 rounded-lg mt-4 hover:bg-blue-600 transition"
                                    >
                                        Subscribe Now
                                    </button>
                                </div>

                            </div>

                            {/* Promo Welcome2026 Card */}
                            {/* <div className="max-w-3xl mx-auto mt-10">
                                <div className="p-6 bg-yellow-100 border border-yellow-300 rounded-lg shadow-md text-center">
                                    <h3 className="text-2xl font-semibold text-yellow-800 mb-2">
                                        🎁 Special Welcome Promo for 2026!
                                    </h3>

                                    <p className="text-gray-700 mb-3">
                                        Use promo code <span className="font-bold text-yellow-900">Welcome2026 </span>
                                         for **free full access** for the next few months.
                                        No credit card required. No hidden conditions.
                                    </p>

                                    <button
                                        onClick={() => {
                                            setPromoCode("Welcome2026");
                                            applyPromo();
                                        }}
                                        className="bg-yellow-500 text-black py-2 px-6 rounded-lg hover:bg-yellow-600 transition font-semibold"
                                    >
                                        Apply Welcome2026
                                    </button>
                                </div>
                            </div> */}

                            {/* Promo Code Input */}
                            <div className="mt-8">
                                <input
                                    type="text"
                                    value={promoCode}
                                    onChange={(e) => setPromoCode(e.target.value)}
                                    placeholder="Enter Promo Code"
                                    className="p-2 border rounded"
                                />
                                <button onClick={applyPromo} className="bg-green-500 text-white py-2 px-6 rounded-lg hover:bg-green-600 transition ml-2">
                                    Apply Promo
                                </button>
                            </div>

                            <div className="mt-8">
                                <section className="py-16 text-center bg-white w-full">
                                    <h3 className="text-2xl font-semibold mb-4">Already Subscribed?</h3>
                                    {renderAuthButtons()}
                                </section>

                            </div>

                            {/* Reset Subscription Button */}
                            {/* <div className="mt-6">
                                <button onClick={resetSubscription} className="bg-red-500 text-white py-2 px-6 rounded-lg hover:bg-red-600 transition">
                                    Reset Subscription
                                </button>
                            </div> */}
                        </section>
                    </>
                )}
            </div>
        </Elements>
    );
};

export default Home;
